import React, { memo, PointerEvent, useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import { Divider, Menu, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import copy from "copy-to-clipboard";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { getAppState } from "stores/modules/app.state/selectors";
import { getHasEditableRole } from "stores/modules/entities.room.members/selectors";
import { addUndoUpdateCharacter } from "stores/modules/entities.room.characters/operations";
import { addUndo } from "stores/modules/entities.room.histories/slice";
import { getAllowDuplicate } from "stores/modules/entities.rooms/selectors";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { addMessage } from "stores/modules/entities.room.messages/operations";
import { appChatStateMutate } from "stores/modules/app.chat/operations";
import { useModal } from "hooks/useModal";
import theme from "theme";

const CharacterMenu = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();

  const uid = useAppSelector((state) => getAppState(state, "uid"));
  const open = useAppSelector((state) =>
    getAppState(state, "openRoomCharacterMenu")
  );
  const top = useAppSelector((state) => getAppState(state, "roomPointerY"));
  const left = useAppSelector((state) => getAppState(state, "roomPointerX"));
  const characterId = useAppSelector((state) =>
    getAppState(state, "openRoomCharacterMenuId")
  );
  const character = useAppSelector((state) =>
    store.getCharacterById(state, characterId ?? "")
  );
  const hasEditableRole = useAppSelector(getHasEditableRole);
  const allowCopyToClipboard = useAppSelector(getAllowDuplicate);
  const {
    open: chatpaletOpen,
    onOpen: onChatpaletOpen,
    onClose: onChatpaletClose,
  } = useModal();
  const chatpaletAnchorEl = useRef<HTMLLIElement>(null);
  const isOwner = uid === character.owner;

  const onClose = useCallback(() => {
    onChatpaletClose();
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomCharacterMenu = false;
      })
    );
  }, [dispatch, onChatpaletClose]);
  const onChangeOwn = useCallback(() => {
    if (characterId == null || uid == null) return;
    dispatch(store.updateCharacterOwn(characterId));
    onClose();
  }, [characterId, dispatch, uid, onClose]);
  const onChangeActive = useCallback(() => {
    if (characterId == null) return;
    dispatch(store.updateCharacterOwn(characterId, false));
    onClose();
  }, [characterId, dispatch, onClose]);
  const onDuplicate = useCallback(() => {
    if (characterId == null) return;
    dispatch(store.duplicateRoomCharacter(characterId));
    onClose();
  }, [characterId, dispatch, onClose]);

  const onCopyToClipboard = useCallback(() => {
    if (characterId == null) return;
    dispatch(store.copyRoomCharacterToClipboard(characterId));
    onClose();
  }, [characterId, dispatch, onClose]);

  const onDelete = useCallback(() => {
    if (characterId == null) return;
    dispatch(store.deleteCurrentRoomItem(characterId));
    onClose();
    if (window.confirm(t("本当に削除しますか？"))) {
      dispatch(store.deleteRoomCharacter(characterId));
      dispatch(
        addUndo({
          kind: "update-character",
          id: characterId,
          before: character,
          after: null,
        })
      );
    }
  }, [characterId, dispatch, onClose, t, character]);
  const onEdit = useCallback(() => {
    if (characterId == null) return;
    if (uid === character.owner || !character.secret || hasEditableRole) {
      dispatch(
        store.appStateMutate((state) => {
          state.openRoomCharacter = true;
          state.openRoomCharacterId = characterId;
        })
      );
    }
    onClose();
  }, [
    character.owner,
    character.secret,
    characterId,
    dispatch,
    hasEditableRole,
    onClose,
    uid,
  ]);
  const onRotate = useCallback(() => {
    if (characterId == null) return;
    dispatch(
      store.updateRoomCharacter(characterId, { angle: character.angle + 45 })
    );
    dispatch(
      addUndoUpdateCharacter(characterId, { angle: character.angle + 45 })
    );
    onClose();
  }, [characterId, dispatch, character.angle, onClose]);
  const onCopyId = useCallback(() => {
    if (characterId == null) return;
    copy(characterId);
    onClose();
  }, [characterId, onClose]);
  const handleEnterChatpaletOpen = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === "mouse") {
        onChatpaletOpen();
      }
    },
    [onChatpaletOpen]
  );

  return (
    <>
      <Menu
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={{ left, top }}
        onClose={onClose}
        MenuListProps={{ dense: true }}
        PaperProps={{
          style: {
            width: "18ch",
          },
        }}
        className={"user-select-none"}
        disableRestoreFocus
      >
        <MenuItem
          onClick={onEdit}
          disabled={
            uid !== character.owner && character.secret && !hasEditableRole
          }
        >
          {t("編集")}
        </MenuItem>
        <MenuItem onClick={onRotate}>
          {t("回転する")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            R or Shift+R
          </ShortcutKeyInfo>
        </MenuItem>
        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
        <MenuItem onClick={onChangeActive}>
          {t("しまう")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            S
          </ShortcutKeyInfo>
        </MenuItem>
        <MenuItem onClick={onChangeOwn}>
          {t("自分の駒にする")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            T
          </ShortcutKeyInfo>
        </MenuItem>
        <MenuItem onClick={onDuplicate}>
          {t("複製")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            Ctrl+D
          </ShortcutKeyInfo>
        </MenuItem>
        <MenuItem onClick={onCopyToClipboard} disabled={!allowCopyToClipboard}>
          {t("コピー")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            Ctrl+C
          </ShortcutKeyInfo>
        </MenuItem>
        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
        <MenuItem
          ref={chatpaletAnchorEl}
          onClick={onChatpaletOpen}
          onPointerEnter={handleEnterChatpaletOpen}
          onPointerLeave={onChatpaletClose}
          disabled={!character.commands || !isOwner}
        >
          {t("チャットパレット")}
          <MenuItemNavIcon>
            <ArrowRightIcon fontSize="small" />
          </MenuItemNavIcon>
        </MenuItem>
        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
        <MenuItem onClick={onDelete}>
          {t("削除")}
          <ShortcutKeyInfo
            color="textSecondary"
            variant="caption"
            align="right"
          >
            Ctrl+
            <BackspaceIcon
              fontSize="inherit"
              style={{ verticalAlign: "middle" }}
            />
          </ShortcutKeyInfo>
        </MenuItem>
        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
        <MenuItem onClick={onCopyId}>{t("Copy Id (for dev)")}</MenuItem>
      </Menu>
      <ChatpaletMenu
        isOpen={chatpaletOpen}
        anchorEl={chatpaletAnchorEl.current}
        onClose={onClose}
        onCloseMenu={onChatpaletClose}
        onOpenMenu={onChatpaletOpen}
        characterId={characterId}
        commands={character.commands}
      />
    </>
  );
};

type ChatpaletMenuProps = {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onCloseMenu: () => void;
  onOpenMenu: () => void;
  characterId: string | null;
  commands: string;
};

const ChatpaletMenu = ({
  isOpen,
  anchorEl,
  onClose,
  onCloseMenu,
  onOpenMenu,
  characterId,
  commands,
}: ChatpaletMenuProps) => {
  const dispatch = useAppDispatch();
  const text = useAppSelector((state) =>
    store.getAppChatState(state, "inputText")
  );
  const roomId = useAppSelector((state) => store.getAppState(state, "roomId"));

  const chatpaletItems = useMemo(() => {
    if (!commands) return [];
    return commands.split(/[\r\n]+/).map((s) => String(s));
  }, [commands]);
  const overrideCharacterId = useAppSelector((state) =>
    store.getAppChatState(state, "overrideCharacterId")
  );

  const handleChatpaletMenu = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === "mouse") {
        onCloseMenu();
      }
    },
    [onCloseMenu]
  );

  const onClickItem = useCallback(
    (selected: string) => {
      if (!roomId) return;
      const lineBreakText = selected.replaceAll("\\n", `\n`);
      if (lineBreakText === text && characterId === overrideCharacterId) {
        dispatch(addMessage(roomId, characterId));
        onClose();
      } else {
        dispatch(
          appChatStateMutate((state) => {
            state.inputText = lineBreakText;
            state.overrideCharacterId = characterId;
          })
        );
      }
    },
    [roomId, characterId, text, dispatch, onClose, overrideCharacterId]
  );

  return (
    <ChatPaletMenu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onCloseMenu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      MenuListProps={{ dense: true, sx: { pointerEvents: "auto" } }}
      hideBackdrop
      sx={{ pointerEvents: "none" }}
      onPointerEnter={onOpenMenu}
      onPointerLeave={handleChatpaletMenu}
    >
      {chatpaletItems.map((item, index) => (
        <MenuItem key={index} onClick={() => onClickItem(item)}>
          <Typography noWrap variant="inherit">
            {item}
          </Typography>
        </MenuItem>
      ))}
    </ChatPaletMenu>
  );
};

const ShortcutKeyInfo = styled(Typography)`
  flex: 1;
`;

const ChatPaletMenu = styled(Menu)`
  .MuiMenu-list {
    pointer-event: auto;
    overflow: auto;
    max-height: 214px;
    max-width: 248px;

    ${theme.breakpoints.down("sm")} {
      max-width: 124px;
    }
  }
`;

const MenuItemNavIcon = styled(Typography)`
  display: inline-flex;
  align-self: center;
  justify-content: end;
  flex: 1;
  width: 100%;
  margin-right: -8px;
`;

export default memo(CharacterMenu);
